import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCompanyFilter } from '../../contexts/CompanyFilterContext';
import {
  getIssues,
  markIgnorePending,
  toUpdatedByString,
  toUpdatedOnString,
} from '../../apis/reconAuditApi';
import { API_FIELDS, toOracleRow } from './issueTypes';
import { IssuesSearchForm } from './IssuesSearchForm';
import { IssuesDataGrid } from './IssuesDataGrid';
import { IgnoreModal } from '../IgnoreModal';

export function IssuesWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedCompany } = useCompanyFilter();
  const [activeTab, setActiveTab] = useState('status-mismatch');
  const [statusMismatchOnly, setStatusMismatchOnly] = useState(
    () => searchParams.get('recon_status') === 'Status Mismatch'
  );
  const [missingDeliveryDateOnly, setMissingDeliveryDateOnly] = useState(
    () => searchParams.get('recon_status') === 'Missing Delivery Date'
  );
  const [ignoreModalOpen, setIgnoreModalOpen] = useState(false);
  const [currentIssue, setCurrentIssue] = useState(null);
  const [currentIssueInitialComment, setCurrentIssueInitialComment] = useState('');
  const [pendingIgnoreIds, setPendingIgnoreIds] = useState(new Set());

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rowCountState, setRowCountState] = useState(0);

  const [startDate, setStartDate] = useState('2020-01-01');
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  });
  const [orderNumber, setOrderNumber] = useState('');
  const [sotNumber, setSotNumber] = useState('');
  const [recipientName, setRecipientName] = useState('');

  const tabs = [
    { id: 'status-mismatch', label: 'Status Mismatch' },
    { id: 'missing-delivery', label: 'Missing Delivery Date' },
  ];

  const fetchIssues = useCallback(async () => {
    const page = paginationModel.page + 1;
    const params = {
      page, page_size: paginationModel.pageSize, sort_by: 'updated_on', sort_dir: 'desc',
      company: selectedCompany?.trim() || undefined,
      order_num: orderNumber?.trim() || undefined,
      item_number: sotNumber?.trim() || undefined,
      comments_like: recipientName?.trim() || undefined,
      from_date: startDate?.trim() ? startDate.trim().slice(0, 10) : undefined,
      to_date: endDate?.trim() ? endDate.trim().slice(0, 10) : undefined,
      active_flag: 1,
    };
    if (statusMismatchOnly) params.recon_status = 'Status Mismatch';
    else if (missingDeliveryDateOnly) params.recon_status = 'Missing Delivery Date';
    setLoading(true);
    setError(null);
    try {
      const res = await getIssues(params);
      if (!res || typeof res !== 'object') { setError('Invalid response from server'); setRows([]); setTotal(0); return; }
      const rawRows = res.rows;
      if (!Array.isArray(rawRows)) { setError('Invalid response: rows missing or not an array'); setRows([]); setTotal(0); return; }
      const normalized = rawRows.map((r) => toOracleRow(r));
      const activeOnly = normalized.filter((row) => row[API_FIELDS.ACTIVE_FLAG] === 1);
      setRows(activeOnly);
      const totalCount = typeof res.total === 'number' ? res.total : 0;
      setTotal(totalCount);
      setRowCountState((prev) => totalCount !== undefined ? totalCount : prev);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load issues');
      setRows([]); setTotal(0);
    } finally { setLoading(false); }
  }, [paginationModel.page, paginationModel.pageSize, selectedCompany, orderNumber, sotNumber, recipientName, startDate, endDate, statusMismatchOnly, missingDeliveryDateOnly]);

  useEffect(() => {
    const reconStatus = searchParams.get('recon_status');
    setStatusMismatchOnly(reconStatus === 'Status Mismatch');
    setMissingDeliveryDateOnly(reconStatus === 'Missing Delivery Date');
  }, [searchParams]);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);
  useEffect(() => { setRowCountState((prev) => (total !== undefined ? total : prev)); }, [total]);

  const handleIgnoreClick = useCallback((row) => {
    const reconId = row[API_FIELDS.RECON_ID];
    if (reconId == null || typeof reconId !== 'number') return;
    const pendingVal = row[API_FIELDS.PENDING_APPROVAL];
    const isPending = (typeof pendingVal === 'string' && pendingVal === 'PENDING') || pendingIgnoreIds.has(reconId);
    if (isPending) return;
    const rawComment = row[API_FIELDS.COMMENTS];
    const existingComment = typeof rawComment === 'string' ? rawComment : rawComment != null ? String(rawComment) : '';
    setCurrentIssue(reconId);
    setCurrentIssueInitialComment(existingComment);
    setIgnoreModalOpen(true);
  }, [pendingIgnoreIds]);

  const handleIgnoreSubmit = useCallback(async (reconId, comments) => {
    setPendingIgnoreIds((prev) => new Set(prev).add(reconId));
    setError(null);
    try {
      const res = await markIgnorePending(reconId, comments);
      setRows((prev) => prev.map((r) => {
        if (r[API_FIELDS.RECON_ID] !== reconId) return r;
        const next = { ...r, [API_FIELDS.PENDING_APPROVAL]: 'PENDING' };
        if (res.updatedBy !== undefined) next[API_FIELDS.UPDATED_BY] = toUpdatedByString(res.updatedBy) ?? null;
        if (res.updatedOn !== undefined) next[API_FIELDS.UPDATED_ON] = toUpdatedOnString(res.updatedOn) ?? null;
        return next;
      }));
      setIgnoreModalOpen(false); setCurrentIssue(null); setCurrentIssueInitialComment('');
    } catch (err) {
      const message = (err instanceof Error && err.message) || 'Failed to submit ignore request';
      setError(message);
      throw err;
    } finally {
      setPendingIgnoreIds((prev) => { const next = new Set(prev); next.delete(reconId); return next; });
    }
  }, []);

  const handleSearch = () => { setPaginationModel((prev) => ({ ...prev, page: 0 })); };
  const onPaginationModelChange = useCallback((model) => { setPaginationModel(model); }, []);

  const handleTabClick = (tabId) => {
    if (tabId === 'status-mismatch') {
      setActiveTab('status-mismatch');
      const next = new URLSearchParams(searchParams);
      if (statusMismatchOnly) { next.delete('recon_status'); next.delete('activeOnly'); }
      else { next.set('recon_status', 'Status Mismatch'); }
      setSearchParams(next, { replace: true });
    } else if (tabId === 'missing-delivery') {
      setActiveTab('missing-delivery');
      const next = new URLSearchParams(searchParams);
      if (missingDeliveryDateOnly) { next.delete('recon_status'); next.delete('activeOnly'); }
      else { next.set('recon_status', 'Missing Delivery Date'); }
      setSearchParams(next, { replace: true });
    } else { setActiveTab(tabId); }
  };

  return (
    <div>
      {/* Issue-type filter tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '15px' }}>
        {tabs.map((tab) => {
          const isStatusMismatchTab = tab.id === 'status-mismatch';
          const isMissingDeliveryTab = tab.id === 'missing-delivery';
          const isActive = (isStatusMismatchTab && statusMismatchOnly) || (isMissingDeliveryTab && missingDeliveryDateOnly) || activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              style={{
                padding: '8px 20px', fontSize: '13px', fontWeight: isActive ? 700 : 400,
                color: isActive ? '#fff' : '#0039A6',
                backgroundColor: isActive ? '#0098DB' : '#f8f9fa',
                border: '1px solid #ddd', borderBottom: isActive ? '2px solid #0039A6' : '1px solid #ddd',
                borderRadius: '4px 4px 0 0', cursor: 'pointer',
                transition: 'background-color 0.2s, color 0.2s',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <IssuesSearchForm
        startDate={startDate} endDate={endDate} orderNumber={orderNumber}
        sotNumber={sotNumber} recipientName={recipientName}
        onStartDateChange={setStartDate} onEndDateChange={setEndDate}
        onOrderNumberChange={setOrderNumber} onSotNumberChange={setSotNumber}
        onRecipientNameChange={setRecipientName} onSearch={handleSearch}
      />

      {error && (
        <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', color: '#991b1b', fontSize: '13px', marginBottom: '15px' }}>
          {error}
        </div>
      )}

      {!error && !loading && rows.length === 0 && (
        <div style={{ padding: '12px 16px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '4px', color: '#555', fontSize: '13px', marginBottom: '15px' }}>
          No issues found. Ensure the backend is running and returns data.
        </div>
      )}

      <IssuesDataGrid
        rows={rows} setRows={setRows} loading={loading}
        rowCountState={rowCountState} paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pendingIgnoreIds={pendingIgnoreIds} onIgnoreClick={handleIgnoreClick}
      />

      <IgnoreModal
        isOpen={ignoreModalOpen}
        onClose={() => { setIgnoreModalOpen(false); setCurrentIssue(null); setCurrentIssueInitialComment(''); }}
        issueId={currentIssue != null ? String(currentIssue) : null}
        initialComment={currentIssueInitialComment}
        onSubmit={handleIgnoreSubmit}
      />
    </div>
  );
}
